import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { getData } from 'features/dinamic';
import { setLoading } from 'features/loader';
import { setDataToast, setVisibleToast } from 'features/toast';
import { selectToken } from 'features/auth';
import { getTokenPayload, extractIdUsuarioFromPayload, extractUsuarioFromPayload } from 'helpers/token';

interface StudentPersonal {
  primerNombre: string;
  segundoNombre: string;
  tercerNombre: string;
  primerApellido: string;
  segundoApellido: string;
  cui: string;
  numeroTelefono: string;
  idGenero: number | null;
  fechaNacimiento: string | null;
  idOcupacion: number | null;
  detalleOcupacion: string | null;
  fileImage: string | null;
}

interface StudentContacto {
  idDepartamento: number | null;
  idMunicipio: number | null;
  direccion: string;
  zona: string;
  correo: string;
  telefonoPrincipal: string;
  telefonoSecundario: string;
}

interface StudentEscolar {
  idGrado: number | null;
  idPromedio: number | null;
  idTipoCentro: number | null;
  nombreCentroEducativo: string;
}

interface StudentAdicional {
  idRelacion: number | null;
  motivoRelacion: string;
  idActividadExtra: number | null;
  motivoActividadExtra: string;
}

interface StudentEncargado {
  primerNombre: string;
  segundoNombre: string;
  primerApellido: string;
  segundoApellido: string;
  cui: string;
  correo: string;
  telefonoPrincipal: string;
  telefonoSecundario: string;
  idParentesco: number | null;
  idOcupacion: number | null;
}

interface StudentIdioma {
  idIdioma: number | null;
  nombre?: string;
}

interface CompletionState {
  datosCompletos: boolean;
  tienePersonal: boolean;
  tieneContacto: boolean;
  tieneEscolar: boolean;
  tieneAdicional: boolean;
  tieneIdioma: boolean;
  tieneEncargado: boolean;
}

interface AlumnoProfileState {
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  idUsuario: number | null;
  usuario: string | null;
  completion: CompletionState;
  personal: StudentPersonal;
  contacto: StudentContacto;
  escolar: StudentEscolar;
  adicional: StudentAdicional;
  encargados: StudentEncargado[];
  idiomas: StudentIdioma[];
}

const emptyPersonal: StudentPersonal = {
  primerNombre: '',
  segundoNombre: '',
  tercerNombre: '',
  primerApellido: '',
  segundoApellido: '',
  cui: '',
  numeroTelefono: '',
  idGenero: null,
  fechaNacimiento: null,
  idOcupacion: null,
  detalleOcupacion: null,
  fileImage: null,
};

const emptyContacto: StudentContacto = {
  idDepartamento: null,
  idMunicipio: null,
  direccion: '',
  zona: '',
  correo: '',
  telefonoPrincipal: '',
  telefonoSecundario: '',
};

const emptyEscolar: StudentEscolar = {
  idGrado: null,
  idPromedio: null,
  idTipoCentro: null,
  nombreCentroEducativo: '',
};

const emptyAdicional: StudentAdicional = {
  idRelacion: null,
  motivoRelacion: '',
  idActividadExtra: null,
  motivoActividadExtra: '',
};

const emptyCompletion: CompletionState = {
  datosCompletos: false,
  tienePersonal: false,
  tieneContacto: false,
  tieneEscolar: false,
  tieneAdicional: false,
  tieneIdioma: false,
  tieneEncargado: false,
};

const initialState: AlumnoProfileState = {
  status: 'idle',
  error: null,
  idUsuario: null,
  usuario: null,
  completion: emptyCompletion,
  personal: emptyPersonal,
  contacto: emptyContacto,
  escolar: emptyEscolar,
  adicional: emptyAdicional,
  encargados: [],
  idiomas: [],
};

const resolveAuthInfo = (state: RootState) => {
  const token = selectToken(state);
  const payload = getTokenPayload(token);
  const usuario = extractUsuarioFromPayload(payload);
  const idUsuario = extractIdUsuarioFromPayload(payload);
  return { usuario, idUsuario };
};

const showToast = (dispatch: any, title: string, message: string, type: 'LimeGreen' | 'Crimson') => {
  dispatch(setDataToast({ title, message, data: null, type, time: Date.now() }));
  dispatch(setVisibleToast(true));
};

const parseResponse = (data: any) => {
  if (!data) {
    return null;
  }
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch (error) {
      return null;
    }
  }
  return data;
};

export const fetchAlumnoProfile = createAsyncThunk(
  'alumnoProfile/fetchAlumnoProfile',
  async (_: void, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { usuario: usuarioFromState } = state.alumnoProfile;
    const { usuario: usuarioFromToken, idUsuario } = resolveAuthInfo(state);
    const usuario = usuarioFromState || usuarioFromToken;

    if (!usuario) {
      return rejectWithValue('Usuario no identificado');
    }

    const body = {
      idsp: 230
    };

    dispatch(setLoading(true));
    const { result, message, data } = await getData(body, getState, dispatch);
    dispatch(setLoading(false));

    if (result) {
      return { usuario, idUsuario, payload: parseResponse(data) };
    }

    return rejectWithValue(message || 'No fue posible obtener la información');
  }
);

interface PersonalPayload {
  primerNombre: string;
  segundoNombre?: string;
  tercerNombre?: string;
  primerApellido: string;
  segundoApellido?: string;
  cui: string;
  numeroTelefono: string;
  idGenero: number;
  fechaNacimiento: string | null;
  idOcupacion?: number | null;
  detalleOcupacion?: string | null;
}

export const updateAlumnoPersonal = createAsyncThunk(
  'alumnoProfile/updateAlumnoPersonal',
  async (payload: PersonalPayload, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { usuario: usuarioState } = state.alumnoProfile;
    const { usuario: usuarioToken } = resolveAuthInfo(state);
    const usuario = usuarioState || usuarioToken;

    if (!usuario) {
      return rejectWithValue('Usuario no identificado');
    }

    const body = {
      idsp: 224,
      primerNombre: payload.primerNombre?.trim() || '',
      segundoNombre: payload.segundoNombre?.trim() || null,
      tercerNombre: payload.tercerNombre?.trim() || null,
      primerApellido: payload.primerApellido?.trim() || '',
      segundoApellido: payload.segundoApellido?.trim() || null,
      cui: payload.cui?.trim() || '',
      numeroTelefono: payload.numeroTelefono?.trim() || '',
      idGenero: payload.idGenero || null,
      fechaNacimiento: payload.fechaNacimiento
    };

    dispatch(setLoading(true));
    const { result, message, data } = await getData(body, getState, dispatch);
    dispatch(setLoading(false));

    if (result) {
      showToast(dispatch, 'Información actualizada', 'Tu información personal ha sido guardada', 'LimeGreen');
      await dispatch(fetchAlumnoProfile());
      return true;
    }

    showToast(dispatch, 'Acción incompleta', message || 'No fue posible guardar la información', 'Crimson');
    return rejectWithValue(message || data || 'Error');
  }
);

interface ContactoPayload extends StudentContacto {}

export const updateAlumnoContacto = createAsyncThunk(
  'alumnoProfile/updateAlumnoContacto',
  async (payload: ContactoPayload, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { usuario: usuarioState } = state.alumnoProfile;
    const { usuario: usuarioToken } = resolveAuthInfo(state);
    const usuario = usuarioState || usuarioToken;

    if (!usuario) {
      return rejectWithValue('Usuario no identificado');
    }

    const body = {
      idsp: 225,
      correo: payload.correo?.trim() || null,
      telefonoPrincipal: payload.telefonoPrincipal?.trim() || '',
      telefonoSecundario: payload.telefonoSecundario?.trim() || null,
      direccion: payload.direccion?.trim() || '',
      zona: payload.zona?.trim() || null,
      idDepartamento: payload.idDepartamento || null,
      idMunicipio: payload.idMunicipio || null,
    };

    dispatch(setLoading(true));
    const { result, message, data } = await getData(body, getState, dispatch);
    dispatch(setLoading(false));

    if (result) {
      showToast(dispatch, 'Información actualizada', 'Tus datos de contacto han sido guardados', 'LimeGreen');
      await dispatch(fetchAlumnoProfile());
      return true;
    }

    showToast(dispatch, 'Acción incompleta', message || 'No fue posible guardar la información', 'Crimson');
    return rejectWithValue(message || data || 'Error');
  }
);

interface EscolarPayload extends StudentEscolar {}

export const updateAlumnoEscolar = createAsyncThunk(
  'alumnoProfile/updateAlumnoEscolar',
  async (payload: EscolarPayload, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { usuario: usuarioState } = state.alumnoProfile;
    const { usuario: usuarioToken } = resolveAuthInfo(state);
    const usuario = usuarioState || usuarioToken;

    if (!usuario) {
      return rejectWithValue('Usuario no identificado');
    }

    const body = {
      idsp: 226,
      idGrado: payload.idGrado || null,
      idPromedio: payload.idPromedio || null,
      idTipoCentro: payload.idTipoCentro || null,
      nombreCentroEducativo: payload.nombreCentroEducativo?.trim() || null,
    };

    dispatch(setLoading(true));
    const { result, message, data } = await getData(body, getState, dispatch);
    dispatch(setLoading(false));

    if (result) {
      showToast(dispatch, 'Información actualizada', 'Tu información escolar ha sido guardada', 'LimeGreen');
      await dispatch(fetchAlumnoProfile());
      return true;
    }

    showToast(dispatch, 'Acción incompleta', message || 'No fue posible guardar la información', 'Crimson');
    return rejectWithValue(message || data || 'Error');
  }
);

interface EncargadosPayload {
  encargados: StudentEncargado[];
}

export const replaceAlumnoEncargados = createAsyncThunk(
  'alumnoProfile/replaceAlumnoEncargados',
  async ({ encargados }: EncargadosPayload, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { usuario: usuarioState } = state.alumnoProfile;
    const { usuario: usuarioToken } = resolveAuthInfo(state);
    const usuario = usuarioState || usuarioToken;

    if (!usuario) {
      return rejectWithValue('Usuario no identificado');
    }

    const body = {
      idsp: 227,
      encargados: encargados.map((item) => ({
        primerNombre: item.primerNombre?.trim() || '',
        segundoNombre: item.segundoNombre?.trim() || null,
        primerApellido: item.primerApellido?.trim() || '',
        segundoApellido: item.segundoApellido?.trim() || null,
        cui: item.cui?.trim() || null,
        correo: item.correo?.trim() || null,
        telefonoPrincipal: item.telefonoPrincipal?.trim() || '',
        telefonoSecundario: item.telefonoSecundario?.trim() || null,
        idParentesco: item.idParentesco || null,
        idOcupacion: item.idOcupacion || null,
      })),
    };

    dispatch(setLoading(true));
    const { result, message, data } = await getData(body, getState, dispatch);
    dispatch(setLoading(false));

    if (result) {
      showToast(dispatch, 'Información actualizada', 'Los encargados han sido actualizados', 'LimeGreen');
      await dispatch(fetchAlumnoProfile());
      return true;
    }

    showToast(dispatch, 'Acción incompleta', message || 'No fue posible guardar la información', 'Crimson');
    return rejectWithValue(message || data || 'Error');
  }
);

interface IdiomasPayload {
  idiomas: StudentIdioma[];
}

export const replaceAlumnoIdiomas = createAsyncThunk(
  'alumnoProfile/replaceAlumnoIdiomas',
  async ({ idiomas }: IdiomasPayload, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { usuario: usuarioState } = state.alumnoProfile;
    const { usuario: usuarioToken } = resolveAuthInfo(state);
    const usuario = usuarioState || usuarioToken;

    if (!usuario) {
      return rejectWithValue('Usuario no identificado');
    }

    const body = {
      idsp: 228,
      idiomas: idiomas.filter((idioma) => idioma.idIdioma).map((idioma) => ({ idIdioma: idioma.idIdioma })),
    };

    dispatch(setLoading(true));
    const { result, message, data } = await getData(body, getState, dispatch);
    dispatch(setLoading(false));

    if (result) {
      showToast(dispatch, 'Información actualizada', 'Los idiomas han sido actualizados', 'LimeGreen');
      await dispatch(fetchAlumnoProfile());
      return true;
    }

    showToast(dispatch, 'Acción incompleta', message || 'No fue posible guardar la información', 'Crimson');
    return rejectWithValue(message || data || 'Error');
  }
);

interface AdicionalPayload extends StudentAdicional {}

export const updateAlumnoAdicional = createAsyncThunk(
  'alumnoProfile/updateAlumnoAdicional',
  async (payload: AdicionalPayload, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { usuario: usuarioState } = state.alumnoProfile;
    const { usuario: usuarioToken } = resolveAuthInfo(state);
    const usuario = usuarioState || usuarioToken;

    if (!usuario) {
      return rejectWithValue('Usuario no identificado');
    }

    const body = {
      idsp: 229,
      idRelacion: payload.idRelacion || null,
      motivoRelacion: payload.motivoRelacion?.trim() || null,
      idActividadExtra: payload.idActividadExtra || null,
      motivoActividadExtra: payload.motivoActividadExtra?.trim() || null,
    };

    dispatch(setLoading(true));
    const { result, message, data } = await getData(body, getState, dispatch);
    dispatch(setLoading(false));

    if (result) {
      showToast(dispatch, 'Información actualizada', 'La información adicional ha sido guardada', 'LimeGreen');
      await dispatch(fetchAlumnoProfile());
      return true;
    }

    showToast(dispatch, 'Acción incompleta', message || 'No fue posible guardar la información', 'Crimson');
    return rejectWithValue(message || data || 'Error');
  }
);

const alumnoProfileSlice = createSlice({
  name: 'alumnoProfile',
  initialState,
  reducers: {
    resetAlumnoProfile: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAlumnoProfile.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchAlumnoProfile.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = null;
        state.usuario = action.payload.usuario;
        state.idUsuario = action.payload.idUsuario ?? state.idUsuario;

        const info = action.payload.payload;
        if (!info) {
          state.completion = emptyCompletion;
          state.personal = emptyPersonal;
          state.contacto = emptyContacto;
          state.escolar = emptyEscolar;
          state.adicional = emptyAdicional;
          state.encargados = [];
          state.idiomas = [];
          return;
        }

        state.completion = {
          datosCompletos: Boolean(info.datosCompletos),
          tienePersonal: Boolean(info.tienePersonal),
          tieneContacto: Boolean(info.tieneContacto),
          tieneEscolar: Boolean(info.tieneEscolar),
          tieneAdicional: Boolean(info.tieneAdicional),
          tieneIdioma: Boolean(info.tieneIdioma),
          tieneEncargado: Boolean(info.tieneEncargado),
        };

        state.personal = {
          ...emptyPersonal,
          ...(info.infoPersonal || {}),
          numeroTelefono: info.numeroTelefono || info.telefonoUsuario || (info.infoPersonal || {}).numeroTelefono || '',
        };
        state.contacto = {
          ...emptyContacto,
          ...(info.infoContacto || {}),
        };
        state.escolar = {
          ...emptyEscolar,
          ...(info.infoEscolar || {}),
        };
        state.adicional = {
          ...emptyAdicional,
          ...(info.infoAdicional || {}),
        };
        state.encargados = Array.isArray(info.encargados) ? info.encargados : [];
        state.idiomas = Array.isArray(info.idiomas) ? info.idiomas : [];
      })
      .addCase(fetchAlumnoProfile.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Error al obtener la información';
      })
      .addCase(updateAlumnoPersonal.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateAlumnoPersonal.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(updateAlumnoPersonal.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Error al guardar información personal';
      })
      .addCase(updateAlumnoContacto.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateAlumnoContacto.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(updateAlumnoContacto.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Error al guardar información de contacto';
      })
      .addCase(updateAlumnoEscolar.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateAlumnoEscolar.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(updateAlumnoEscolar.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Error al guardar información escolar';
      })
      .addCase(replaceAlumnoEncargados.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(replaceAlumnoEncargados.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(replaceAlumnoEncargados.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Error al guardar encargados';
      })
      .addCase(replaceAlumnoIdiomas.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(replaceAlumnoIdiomas.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(replaceAlumnoIdiomas.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Error al guardar idiomas';
      })
      .addCase(updateAlumnoAdicional.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateAlumnoAdicional.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(updateAlumnoAdicional.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Error al guardar información adicional';
      });
  },
});

export const { resetAlumnoProfile } = alumnoProfileSlice.actions;

export const selectAlumnoProfile = (state: RootState) => state.alumnoProfile;
export const selectAlumnoPersonal = (state: RootState) => state.alumnoProfile.personal;
export const selectAlumnoContacto = (state: RootState) => state.alumnoProfile.contacto;
export const selectAlumnoEscolar = (state: RootState) => state.alumnoProfile.escolar;
export const selectAlumnoAdicional = (state: RootState) => state.alumnoProfile.adicional;
export const selectAlumnoEncargados = (state: RootState) => state.alumnoProfile.encargados;
export const selectAlumnoIdiomas = (state: RootState) => state.alumnoProfile.idiomas;
export const selectAlumnoCompletion = (state: RootState) => state.alumnoProfile.completion;
export const selectAlumnoProfileStatus = (state: RootState) => state.alumnoProfile.status;
export const selectAlumnoProfileError = (state: RootState) => state.alumnoProfile.error;

export default alumnoProfileSlice.reducer;
